export interface LiveIndicatorProps {
  className?: string;
}

/**
 * A quiet marker for a page that keeps itself current.
 *
 * The job is to answer one question — "am I looking at current numbers, or do I need to reload?"
 * — without competing with the numbers themselves. So: muted text, a 6px dot, no border, no
 * chip, nothing that reads as a control the user is meant to click.
 *
 * The dot pulses on a steady loop rather than flashing once per request. Two reasons: a
 * per-request flash pulls the eye every interval, which is exactly the distraction this is
 * supposed to avoid; and a flash tied to network activity reads as an error indicator the moment
 * a request is slow. A steady pulse says "this is live" and nothing more.
 *
 * `motion-reduce:hidden` drops the ping ring and leaves the static dot, so the meaning survives
 * without the movement.
 */
export function LiveIndicator({ className }: LiveIndicatorProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-text-muted ${className ?? ""}`}
      // Not aria-live: this never changes, so announcing it would be noise. The title carries the
      // detail for anyone who hovers or reads the accessibility tree.
      title="This page refreshes automatically"
    >
      <span className="relative flex size-2" aria-hidden="true">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60 motion-reduce:hidden" />
        <span className="relative inline-flex size-2 rounded-full bg-success" />
      </span>
      Live
    </span>
  );
}
